import { HttpHeaders } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthApiService, MeApiService } from '@digilize/shared/data/services/src';
import { UserTerms } from '@digilize/shared/definitions/src/lib/interfaces/UserTerms';
import { AuthService } from '@digilize/shared/feature/modules/auth/src';
import {
  differentThanOtherValidator,
  matchOtherValidator,
  requiredTrueValidator,
} from '@digilize/shared/utils/validators';
import { catchError, forkJoin, map, Observable, of, switchMap, throwError } from 'rxjs';

import { ErrorService } from '../../services/error.service';
import { SharedAccountDataService } from '../../services/shared-account-data.service';

@Component({
  selector: 'bwc-manage-password',
  templateUrl: './manage-password.component.html',
  styleUrls: ['./manage-password.component.scss'],
})
export class ManagePasswordComponent implements OnInit {
  form: FormGroup;
  codeForm: FormGroup;
  updatePassError = false;
  checkYourEmail = false;
  password_changed = false;
  resendEmail = false;
  account_activated = false;
  activation_error = false;
  code_mismatch_error = false;
  resendCooldown = false;
  codeResent = false;
  inputsFields: string[] = ['', '', '', '', '', ''];
  codeChars: string[] = ['', '', '', '', '', ''];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: Data,
    private fb: FormBuilder,
    public errorService: ErrorService,
    private meApiService: MeApiService,
    private authApiService: AuthApiService,
    private authService: AuthService,
    public dialogRef: MatDialogRef<ManagePasswordComponent>,
    private sharedAccountDataService: SharedAccountDataService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (!this.data.noResendCode) {
      this.resendActivationLink();
    }
    this.checkData();
    this.createForms();
  }

  checkData() {
    if (this.data.account_activated) {
      this.account_activated = true;
    }
    if (this.data.activation_error) {
      this.activation_error = true;
    }
    if (this.data.resendEmail) {
      this.resendEmail = true;
    }
    if (this.data.checkYourEmail) {
      this.checkYourEmail = true;
    }
  }

  createForms() {
    if (this.data.type === 'change') {
      this.form = this.fb.group({
        password: ['', Validators.required],
        email: [this.data.email],
        password_new: ['', [Validators.required, differentThanOtherValidator('password')]],
        password_confirm: ['', [Validators.required, matchOtherValidator('password_new')]],
      });
    } else {
      if (this.data.type === 'create') {
        this.form = this.fb.group({
          email: [this.data.email],
          password_new: ['', [Validators.required, Validators.minLength(6)]],
          password_confirm: ['', [Validators.required, matchOtherValidator('password_new')]],
          privacy_policy: [false, [requiredTrueValidator()]],
        });
      } else {
        this.form = this.fb.group({
          password: ['', Validators.required],
          email: [this.data.email],
          password_new: ['', Validators.required],
          password_confirm: ['', Validators.required],
        });
      }
    }
    this.codeForm = this.fb.group({
      dig1: ['', Validators.required],
      dig2: ['', Validators.required],
      dig3: ['', Validators.required],
      dig4: ['', Validators.required],
      dig5: ['', Validators.required],
    });
  }

  createPassword() {
    const terms: UserTerms = {
      agreement1: true,
      agreement2: false,
      agreement3: false,
    };

    forkJoin({
      updatePassword: this.meApiService.updatePassword(this.form.value),
      updateTerms: this.meApiService.updateTerms(terms),
    })
      .pipe(
        catchError((error) => {
          return throwError('Error during creating password: ', error);
        })
      )
      .pipe(
        switchMap(() => {
          this.sharedAccountDataService.updateCv('password_created', true);
          return this.getValidToken$();
        })
      )
      .subscribe({
        next: () => {
          this.checkYourEmail = true;
        },
        error: (err) => {
          console.error('Error during activation: ', err);
        },
      });
  }

  changePassword() {
    this.updatePassError = false;
    this.meApiService
      .updatePassword(this.form.value)
      .pipe(
        catchError((error) => {
          this.updatePassError = true;
          return throwError(error);
        })
      )
      .subscribe((resp) => {
        this.password_changed = true;
        setTimeout(() => {
          this.dialogRef.close();
        }, 2000);
      });
  }

  resendActivationLink(showFeedback = false) {
    if (showFeedback) {
      this.codeResent = false;
      this.resendCooldown = true;
      setTimeout(() => {
        this.resendCooldown = false;
        this.codeResent = false;
      }, 3000);
    }
    this.getValidToken$()
      .pipe(
        switchMap((token) => {
          const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
          return this.authApiService.activateProfileAndSendMailWithCode(headers);
        }),
        catchError((error) => {
          return throwError(error);
        })
      )
      .subscribe(() => {
        if (showFeedback) {
          this.codeResent = true;
        }
      });
  }

  getFormControl(formControlName: string) {
    return this.form.get(formControlName) as FormControl;
  }

  getCodeFormControl(formControlName: string) {
    return this.codeForm.get(formControlName) as FormControl;
  }

  activateAccount() {
    const code = this.codeChars.join('');

    this.getValidToken$()
      .pipe(
        switchMap((token) => {
          const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
          return this.authApiService.activateProfileWithToken({ activate_token: code }, headers);
        }),
        catchError((err) => {
          if (err.error.error.info.field === 'activate_token' && err.error.error.info.kind === 'mismatch') {
            this.code_mismatch_error = true;
          }
          return throwError('Error during activateProfileWithToken', err);
        })
      )
      .subscribe((data) => {
        this.checkYourEmail = false;
        this.account_activated = true;
        this.sharedAccountDataService.loadCv().subscribe();
      });
  }

  onActivatedContinue(): void {
    this.dialogRef.close({ activated: true });
    const redirectTo = sessionStorage.getItem('redirectAfterActivation');
    if (redirectTo === 'vacancies') {
      sessionStorage.removeItem('redirectAfterActivation');
      this.router.navigate(['profile-check', 'vacancies']);
    }
  }

  close() {
    this.dialogRef.close();
  }

  private getValidToken$(): Observable<string> {
    const token = this.authService.getAccessToken();
    if (token) {
      return of(token);
    }
    return this.authService.refreshAccessToken().pipe(
      map(() => {
        const refreshedToken = this.authService.getAccessToken();
        if (!refreshedToken) {
          throw new Error('Unable to obtain access token');
        }
        return refreshedToken;
      })
    );
  }

  onInput(index: number, event: any): void {
    const text = event.target.value;
    if (text.length === 1) {
      this.codeChars[index] = text;
    }

    if (event.target.value !== '') {
      if (index < this.inputsFields.length - 1) {
        const nextIndex = index + 1;
        const nextInputId = `input_${nextIndex}`;
        const nextInput = document.getElementById(nextInputId);
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  }

  onPaste(index: number, event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text');
    if (pastedText) {
      const pastedChars = pastedText.slice(0, 6).split('');

      pastedChars.forEach((char, i) => {
        if (index + i < this.codeChars.length) {
          this.codeChars[index + i] = char;
        }
      });
    }
  }
}

export interface Data {
  type: string;
  email: string;
  password_created: boolean;
  resendEmail: boolean;
  checkYourEmail: boolean;
  account_activated: boolean;
  activation_error: boolean;
  noResendCode?: boolean;
}
